import React, { useEffect, useState } from "react";
import { Save, Plus, X, Loader2 } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText, fieldErrors } from "@/lib/devadminApi";

const ROUND_FIELDS = [["rounds_count", "Rounds", "Conclave rounds in the day"], ["round_duration_min", "Round duration (min)", "Minutes per round"], ["table_size", "Table size", "Seats per round table"]];

export function CategoriesRounds({ eventId }) {
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState([]);
  const [conclave, setConclave] = useState({});
  const [pricing, setPricing] = useState({});
  const [invoice, setInvoice] = useState({});
  const [newCat, setNewCat] = useState("");
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCfg(null);
    api.get(`/events/${eventId}/config`).then((r) => {
      setCfg(r.data); setCats(r.data.business_categories || []); setConclave(r.data.conclave || {}); setPricing(r.data.pricing || {}); setInvoice(r.data.invoice || {});
    }).catch((e) => toast.error(errText(e)));
  }, [eventId]);

  const addCat = () => {
    const v = newCat.trim();
    if (!v) return;
    if (cats.some((c) => c.toLowerCase() === v.toLowerCase())) return toast.error(`"${v}" already exists`);
    setCats([...cats, v].sort((a, b) => a.localeCompare(b))); setNewCat("");
  };

  const save = async () => {
    setBusy(true); setErrs({});
    // Only coerce integer-typed round fields on THIS screen — booleans like
    // `allow_same_category_when_forced` are managed on the Conclave Rules tab; passing them
    // through Number() turns true/false into 1/0 and the backend rejects it as "must be boolean".
    const INT_KEYS = ["rounds_count", "round_duration_min", "table_size"];
    const conclavePatch = Object.fromEntries(
      Object.entries(conclave).map(([k, v]) => [k, INT_KEYS.includes(k) ? Number(v) : v])
    );
    const patch = {
      business_categories: cats,
      conclave: conclavePatch,
      pricing: { ...pricing, gst_pct: Number(pricing.gst_pct), gst_inclusive: !!pricing.gst_inclusive, sac_code: String(pricing.sac_code || "") },
      invoice,
    };
    try {
      const { data } = await api.patch(`/events/${eventId}/config`, patch);
      setCfg(data); toast.success("Categories & rounds saved · audit logged");
    } catch (e) { setErrs(fieldErrors(e)); toast.error(errText(e)); } finally { setBusy(false); }
  };

  if (!cfg) return <div className="mono-label" data-testid="config-loading">Loading config…</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Panel className="p-6 lg:col-span-2" data-testid="categories-panel">
        <div className="flex items-center justify-between">
          <MonoLabel>Business Categories · {cats.length}</MonoLabel>
        </div>
        <div className="mt-4 flex items-stretch">
          <input data-testid="category-input" className="input-x flex-1" placeholder="Add a category, press Enter" value={newCat}
            onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} />
          <button data-testid="category-add-btn" onClick={addCat} className="btn-ghost !px-4 border-l-0"><Plus size={14} /></button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="category-chips">
          {cats.map((c) => (
            <span key={c} data-testid={`category-chip-${c.replace(/\s+/g, "-").toLowerCase()}`} className="chip inline-flex items-center gap-2">
              {c}
              <button aria-label={`remove ${c}`} onClick={() => setCats(cats.filter((x) => x !== c))} className="hover:text-[var(--red)]"><X size={12} /></button>
            </span>
          ))}
        </div>
        {errs.business_categories && <div className="text-xs mt-2" style={{ color: "var(--red-2)" }}>{errs.business_categories}</div>}
      </Panel>

      <Panel className="p-6" data-testid="rounds-panel">
        <MonoLabel>Conclave Rounds</MonoLabel>
        <div className="mt-4 space-y-4">
          {ROUND_FIELDS.map(([k, label, hint]) => (
            <div key={k}>
              <MonoLabel>{label}</MonoLabel>
              <input data-testid={`conclave-${k}`} className="input-x mt-2 !font-mono !w-32" value={conclave[k] ?? ""}
                onChange={(e) => setConclave({ ...conclave, [k]: e.target.value.replace(/\D/g, "") })} />
              <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{hint}</div>
            </div>
          ))}
          {errs.conclave && <div data-testid="conclave-error" className="text-xs" style={{ color: "var(--red-2)" }}>{errs.conclave}</div>}
        </div>
        <div className="mt-5 p-3 hairline text-sm" style={{ color: "var(--taupe)" }}>
          {Number(conclave.rounds_count || 0)} rounds × {Number(conclave.round_duration_min || 0)} min = <span style={{ color: "var(--text)" }}>{Number(conclave.rounds_count || 0) * Number(conclave.round_duration_min || 0)} min</span> of conclave
        </div>
      </Panel>

      <Panel className="p-6 lg:col-span-3" data-testid="pricing-panel">
        <MonoLabel>Pricing · GST</MonoLabel>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <MonoLabel>GST %</MonoLabel>
            <input data-testid="pricing-gst_pct" className="input-x mt-2 !font-mono !w-24" value={pricing.gst_pct ?? ""} onChange={(e) => setPricing({ ...pricing, gst_pct: e.target.value.replace(/\D/g, "") })} />
          </div>
          <div>
            <MonoLabel>SAC code</MonoLabel>
            <input data-testid="pricing-sac_code" className="input-x mt-2 !font-mono !w-32" value={pricing.sac_code ?? ""} onChange={(e) => setPricing({ ...pricing, sac_code: e.target.value.replace(/\D/g, "") })} />
          </div>
          <div>
            <MonoLabel>Tier fee is</MonoLabel>
            <div className="mt-2 inline-flex hairline">
              {[[false, "Ex-GST (add on top)"], [true, "GST inclusive"]].map(([v, l]) => (
                <button key={String(v)} data-testid={`pricing-inclusive-${v}`} onClick={() => setPricing({ ...pricing, gst_inclusive: v })}
                  className={`px-4 py-2 text-xs mono-label ${!!pricing.gst_inclusive === v ? "bg-[var(--red)] text-[var(--text)]" : "text-[var(--taupe)]"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        {errs.pricing && <div className="text-xs mt-2" style={{ color: "var(--red-2)" }}>{errs.pricing}</div>}
      </Panel>

      <Panel className="p-6 lg:col-span-3" data-testid="invoice-panel">
        <div className="flex items-center justify-between">
          <MonoLabel>Invoice · Issuer details (printed on the PDF sent with the confirmation)</MonoLabel>
          <a data-testid="invoice-sample-link" className="mono-label hover:text-[var(--red)]" href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/sample.pdf`} target="_blank" rel="noreferrer">Preview sample PDF ↗</a>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[["issuer_name", "Issuer name"], ["gstin", "GSTIN"], ["start_time", "Event start time ({{4}} in WhatsApp)"], ["issuer_address", "Issuer address"], ["place_of_supply", "Place of supply"], ["footer", "Footer line"]].map(([k, label]) => (
            <div key={k} className={k === "issuer_address" ? "sm:col-span-2" : ""}>
              <MonoLabel>{label}</MonoLabel>
              <input data-testid={`invoice-${k}`} className="input-x mt-2" value={invoice[k] ?? ""} onChange={(e) => setInvoice({ ...invoice, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        {errs.invoice && <div className="text-xs mt-2" style={{ color: "var(--red-2)" }}>{errs.invoice}</div>}
      </Panel>

      <button data-testid="config-save-btn" className="btn-red lg:col-span-3 w-fit" disabled={busy} onClick={save}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save categories & rounds
      </button>
    </div>
  );
}
